import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Button, ConfigProvider, Drawer, Modal, Popconfirm, Popover, Radio, Space, Spin, Table, Tag, Tooltip, message,
} from "antd";
import {
  CloseOutlined, MailOutlined, PhoneOutlined, GlobalOutlined, EnvironmentOutlined,
  ShopOutlined, TeamOutlined, MessageOutlined, UserOutlined, SendOutlined,
  LinkOutlined, FormOutlined, EditOutlined, RedoOutlined, StopOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";

import * as api from "./controller";
import ContactCarousel from "./ContactCarousel";
import InviteCustomerModal from "./InviteCustomerModal";
import LeadTagPicker, { TagChips } from "./LeadTagPicker";
import LeadFormDrawer, { TASK_OPTIONS, QUESTIONS } from "./LeadFormDrawer";
import BookingEmailModal from "./BookingEmailModal";
import EmailFeed from "./EmailFeed";
import "./crm.css";
import "./leads.css";

// ── Shared vocabulary (CrmLeads imports these so the table and the drawer
//    never disagree about what a status or a source is called) ─────────────

export const STATUS_LABEL = {
  new: "New",
  invited: "Invited",
  registered: "Registered",
  converted: "Converted",
};

export const SOURCE_LABEL = {
  framer: "Website form",
  manual: "By hand",
  backfill: "Existing customer",
  email: "From email",
};

// A lead the CRM made from email, not yet signed up or invited: the only kind
// "Not a lead" applies to (the server enforces the same).
export function canDismissAsNotALead(lead) {
  return !!lead && lead.source === "email" && !lead.practice && !lead.user
    && !((lead.invites || []).length);
}

// "Not a lead": delete it and ignore its address — or its whole company domain,
// offered only when the lead IS a company domain (never gmail.com).
function NotALeadModal({ lead, open, onClose, onDone }) {
  const [scope, setScope] = useState("address");
  const [busy, setBusy] = useState(false);
  const domain = (lead && lead.email && lead.email.includes("@")) ? lead.email.split("@")[1] : "";
  const isCompany = !!domain && lead && lead.label === domain;
  useEffect(() => { if (open) setScope("address"); }, [open]);
  const addresses = lead
    ? Array.from(new Set([lead.email].concat((lead.contacts || []).map((c) => c.email)).filter(Boolean)))
    : [];
  const submit = async () => {
    setBusy(true);
    const r = await api.notALead(lead.id, scope);
    setBusy(false);
    if (r.status >= 200 && r.status < 300) {
      message.success(`Removed. ${scope === "domain" ? `Mail from @${domain}` : "Mail from them"} won't make a lead again.`);
      onDone && onDone();
    } else {
      message.error((r.json && r.json.detail) || "Couldn't remove the lead.");
    }
  };
  return (
    <Modal
      title="Not a lead"
      open={open}
      onCancel={onClose}
      onOk={submit}
      okText="Remove and ignore"
      okButtonProps={{ danger: true }}
      confirmLoading={busy}
      zIndex={10050}
    >
      <p className="crm-sub" style={{ fontSize: 13 }}>
        The lead is deleted. Its emails stay, filed under nobody. Future mail
        from the address{addresses.length > 1 ? "es" : ""} you choose never makes a lead.
      </p>
      <Radio.Group value={scope} onChange={(e) => setScope(e.target.value)} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <Radio value="address">Ignore {addresses.join(", ") || "this address"}</Radio>
        {isCompany ? <Radio value="domain">Ignore everyone at @{domain}</Radio> : null}
      </Radio.Group>
    </Modal>
  );
}

export function LeadStatusPill({ status }) {
  const key = STATUS_LABEL[status] ? status : "new";
  return <span className={`crml-pill ${key}`}>{STATUS_LABEL[key]}</span>;
}

export function DormantPill() {
  return <span className="crml-pill dormant">Dormant</span>;
}

export function principalOf(lead) {
  return ((lead && lead.contacts) || []).find((c) => c.role === "principal_dentist") || null;
}

export function initialOf(name) {
  const s = (name || "").trim();
  return s ? s.charAt(0).toUpperCase() : "?";
}

const fmtDay = (d) => (d ? dayjs(d).format("MMM D, YYYY") : "—");
const fmtWhen = (d) => (d ? dayjs(d).format("MMM D, YYYY · HH:mm") : "—");

// The Invites tab derives the same four states the same way; copied rather
// than imported so this drawer doesn't depend on a table component.
function inviteStatus(r) {
  if (r.state === "accepted" || r.is_used) return { key: "accepted", label: "Accepted" };
  if (r.state === "cancelled") return { key: "cancelled", label: "Cancelled" };
  const exp = r.expiration_date ? new Date(r.expiration_date) : null;
  if (exp && !Number.isNaN(exp.getTime()) && exp.getTime() < Date.now()) {
    return { key: "expired", label: "Expired" };
  }
  return { key: "pending", label: "Pending" };
}

// Payload keys the lead already carries as first-class fields. A website form
// posts with its own labels ("Practice Name", "principal-dentist-email"), so
// keys are normalised before matching; anything left over is an answer we have
// nowhere else to show, and is what the "Other answers" table is for.
const MAPPED_KEYS = [
  "practice_name", "name", "website", "is_multi_clinic", "multi_clinic", "clinic_count", "pms",
  "frontdesk_staff_count", "insurance_coordinator_count", "treatment_coordinator_count",
  "email", "phone", "address", "city", "state", "province", "zip_code", "postal_code", "zip",
  "country", "tasks_wanted", "tasks", "tasks_other", "current_workflow", "breakdown_recording",
  "team_involved", "communication_method", "principal_dentist", "principal_dentist_name",
  "principal_dentist_email", "principal_dentist_phone", "principal_name", "principal_email",
  "principal_phone", "dentist_name", "dentist_email", "dentist_phone",
];
const normKey = (k) => String(k || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
const isMapped = (k) => MAPPED_KEYS.indexOf(normKey(k)) !== -1;

const fmtValue = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (Array.isArray(v)) return v.map(fmtValue).join(", ");
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
};

function Section({ icon, title, subtitle, count, extra, children }) {
  return (
    <div className="crm-section">
      <div className="crm-section-head">
        <span className="crm-section-icon">{icon}</span>
        <span className="crm-section-title">{title}</span>
        {subtitle ? <span className="crm-section-sub">{subtitle}</span> : null}
        {typeof count === "number" ? <span className="crm-section-count">{count}</span> : null}
        {extra ? <span style={{ marginLeft: typeof count === "number" ? 8 : "auto" }}>{extra}</span> : null}
      </div>
      <div className="crm-section-body">{children}</div>
    </div>
  );
}

function KV({ rows }) {
  return (
    <div className="crml-kv">
      {rows.map(([k, v]) => {
        const empty = v === null || v === undefined || v === "" || v === "—";
        return (
          <React.Fragment key={k}>
            <div className="crml-kv-k">{k}</div>
            <div className={`crml-kv-v${empty ? " is-empty" : ""}`}>{empty ? "—" : v}</div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

// The "Edit tags" popover: a picker plus an explicit Save. Typing a new tag
// name creates a global tag, so it must not happen on every keystroke.
function TagsPopover({ lead, onSaved, children }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({ tagIds: [], newTags: [] });
  const [busy, setBusy] = useState(false);

  // Seed on open only — a reload of the lead behind the popover must not wipe
  // a half-made selection.
  useEffect(() => {
    if (open) setDraft({ tagIds: (lead.tags || []).map((t) => t.id), newTags: [] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const save = async () => {
    setBusy(true);
    const r = await api.setLeadTags(lead.id, draft);
    setBusy(false);
    if (r.status >= 200 && r.status < 300) {
      onSaved(r.json && Array.isArray(r.json.tags) ? r.json.tags : []);
      setOpen(false);
    } else {
      message.error((r.json && r.json.detail) || "Couldn't save the tags.");
    }
  };

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger="click"
      placement="bottomLeft"
      zIndex={10050}
      content={
        <div className="crml-tag-pop">
          <LeadTagPicker value={draft.tagIds} onChange={setDraft} size="small" />
          <div className="crml-tag-pop-foot">
            <Button size="small" onClick={() => setOpen(false)}>Cancel</Button>
            <Button size="small" type="primary" loading={busy} onClick={save}>Save</Button>
          </div>
        </div>
      }
    >
      {children}
    </Popover>
  );
}

// One website submission: the answers we have no field for, then the whole
// payload folded away for when the mapping itself is in question.
function Submission({ s }) {
  const [raw, setRaw] = useState(false);
  const payload = s.payload && typeof s.payload === "object" ? s.payload : {};
  const other = Object.keys(payload).filter((k) => !isMapped(k));
  return (
    <div className="crml-submission">
      <div className="crml-submission-head">
        <span className="crml-submission-title">Received {fmtWhen(s.received_at)}</span>
        {s.submission_id ? <span className="crm-sub">#{s.submission_id}</span> : null}
      </div>
      {other.length ? (
        <>
          <div className="crml-sub-heading">Other answers</div>
          <Table
            size="small"
            rowKey="k"
            pagination={false}
            showHeader={false}
            columns={[
              { dataIndex: "k", width: 220, render: (v) => <span className="crm-sub">{v}</span> },
              { dataIndex: "v", render: (v) => <span style={{ whiteSpace: "pre-wrap" }}>{v}</span> },
            ]}
            dataSource={other.map((k) => ({ k, v: fmtValue(payload[k]) }))}
          />
        </>
      ) : (
        <div className="crm-sub">Every answer in this submission is already on the lead.</div>
      )}
      <button type="button" className="crml-fold" onClick={() => setRaw((v) => !v)}>
        {raw ? "Hide raw submission" : "Raw submission"}
      </button>
      {raw ? <pre className="crml-raw">{JSON.stringify(payload, null, 2)}</pre> : null}
    </div>
  );
}

/**
 * Everything we know about one lead, and every action that moves it along:
 * invite them, send the booking email, edit, delete, tag.
 *
 * `onChanged` fires after any mutation so the table behind can refetch; the
 * drawer itself reloads its own detail. `onOpenPractice` / `onOpenOffice` hand
 * off to the Practices drawers once the lead has become a customer — the lead
 * stops being the place you look after that, and the link says so.
 */
export default function LeadDetailDrawer({ leadId, onClose, onChanged, onOpenPractice, onOpenOffice, initialMessageId }) {
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [notLeadOpen, setNotLeadOpen] = useState(false);
  const [resending, setResending] = useState(null);
  const conversationRef = useRef(null);

  const load = useCallback(async () => {
    if (!leadId) return;
    setLoading(true);
    const r = await api.getLead(leadId);
    setLoading(false);
    if (r.status >= 200 && r.status < 300 && r.json) {
      setLead(r.json);
    } else {
      setLead(null);
      message.error((r.json && r.json.detail) || "Couldn't load the lead.");
    }
  }, [leadId]);

  useEffect(() => {
    setLead(null);
    if (leadId) load();
  }, [leadId, load]);

  // Opened from Today on one email: bring the conversation into view.
  useEffect(() => {
    if (lead && initialMessageId && conversationRef.current && conversationRef.current.scrollIntoView) {
      conversationRef.current.scrollIntoView({ block: "start" });
    }
  }, [lead, initialMessageId]);

  const changed = () => {
    load();
    onChanged && onChanged();
  };

  const remove = async () => {
    const r = await api.deleteLead(lead.id);
    if (r.status >= 200 && r.status < 300) {
      message.success(`${lead.practice_name || "Lead"} deleted.`);
      onChanged && onChanged();
      onClose();
    } else {
      message.error((r.json && r.json.detail) || "Couldn't delete the lead.");
    }
  };

  const copyLink = async (inv) => {
    if (!inv.invite_link) {
      message.error("This invite has no link to copy.");
      return;
    }
    try {
      await navigator.clipboard.writeText(inv.invite_link);
      message.success("Invite link copied.");
    } catch (e) {
      message.warning(inv.invite_link, 6); // clipboard blocked — show it to copy by hand
    }
  };

  const resend = async (inv) => {
    setResending(inv.id);
    const r = await api.resendInvite(inv.id);
    setResending(null);
    if (r.status >= 200 && r.status < 300) {
      message.success(`Invite re-sent to ${inv.receiver_email}.`);
      changed();
    } else {
      message.error((r.json && r.json.detail) || "Couldn't resend the invite.");
    }
  };

  const principal = principalOf(lead);
  const contacts = (lead && lead.contacts) || [];
  const invites = (lead && lead.invites) || [];
  const submissions = (lead && lead.submissions) || [];
  const practice = lead && lead.practice;
  const offices = (lead && lead.offices) || [];
  const canInvite = lead && lead.status !== "converted" && lead.status !== "registered";
  const taskLabel = (v) => {
    const o = TASK_OPTIONS.find((t) => t.value === v);
    return o ? o.label : v;
  };
  const addressLine = lead
    ? [lead.address, [lead.city, lead.state].filter(Boolean).join(", "), lead.zip_code].filter(Boolean).join(" · ")
    : "";

  return (
    <Drawer
      closable={false}
      title={null}
      width={720}
      open={!!leadId}
      onClose={onClose}
      destroyOnClose
      className="crm-drawer"
      bodyStyle={{ padding: 0, background: "#f7f6fb" }}
    >
      <button className="crm-hero-close" onClick={onClose} aria-label="Close"><CloseOutlined /></button>

      {loading || !lead ? (
        <div className="crm-drawer-loading"><Spin size="large" /></div>
      ) : (
        <div className="crm-detail">
          {/* ── Hero ─────────────────────────────────────────────── */}
          <div className="crm-hero">
            <div className="crm-hero-top">
              <div className="crm-hero-avatar">{initialOf(lead.practice_name || lead.label || lead.email)}</div>
              <div className="crm-hero-id">
                <div className="crm-hero-name">{lead.practice_name || lead.label || lead.email || "Unnamed lead"}</div>
                <div className="crml-hero-meta">
                  <span>{SOURCE_LABEL[lead.source] || lead.source_label || lead.source || "—"}</span>
                  <span className="crml-dot">·</span>
                  <span>Created {fmtDay(lead.created_at)}{lead.created_by ? ` by ${lead.created_by}` : ""}</span>
                  {lead.last_submission_at ? (
                    <>
                      <span className="crml-dot">·</span>
                      <span>Last form submission {fmtDay(lead.last_submission_at)}</span>
                    </>
                  ) : null}
                </div>
              </div>
              <span className="crml-pills">
                <LeadStatusPill status={lead.status} />
                {lead.is_dormant ? <DormantPill /> : null}
              </span>
            </div>

            <div className="crml-hero-tags">
              <TagChips tags={lead.tags} max={8} />
              <TagsPopover lead={lead} onSaved={(tags) => { setLead((l) => ({ ...l, tags })); onChanged && onChanged(); }}>
                <Button type="link" size="small" className="crml-edit-tags" icon={<EditOutlined />}>
                  Edit tags
                </Button>
              </TagsPopover>
            </div>

            <div className="crm-hero-contact">
              <span><MailOutlined />{lead.email || "—"}</span>
              <span><PhoneOutlined />{lead.phone || "—"}</span>
              {lead.website ? (
                <span>
                  <GlobalOutlined />
                  <a href={/^https?:\/\//i.test(lead.website) ? lead.website : `https://${lead.website}`} target="_blank" rel="noopener noreferrer">
                    {lead.website}
                  </a>
                </span>
              ) : null}
            </div>
          </div>

          {/* ── Actions ──────────────────────────────────────────── */}
          <div className="crml-actions">
            {canInvite ? (
              <Button type="primary" icon={<SendOutlined />} onClick={() => setInviteOpen(true)}>
                Invite
              </Button>
            ) : null}
            <Button icon={<MailOutlined />} onClick={() => setBookingOpen(true)}>
              Send booking email
            </Button>
            <Button icon={<FormOutlined />} onClick={() => setEditOpen(true)}>Edit</Button>
            {canDismissAsNotALead(lead) ? (
              <Tooltip title="A vendor, a newsletter, a personal contact: remove it and stop their mail making leads." zIndex={10050}>
                <Button icon={<StopOutlined />} onClick={() => setNotLeadOpen(true)}>Not a lead</Button>
              </Tooltip>
            ) : null}
            <Popconfirm
              title="Delete this lead?"
              description="Its contacts, submissions and email history go with it."
              okText="Delete"
              okButtonProps={{ danger: true }}
              cancelText="Cancel"
              zIndex={10050}
              onConfirm={remove}
            >
              <Button danger>Delete</Button>
            </Popconfirm>
          </div>

          {/* ── Contact & address ────────────────────────────────── */}
          <Section icon={<EnvironmentOutlined />} title="Contact & address">
            <KV
              rows={[
                ["Principal dentist", principal ? [principal.name, principal.email, principal.phone].filter(Boolean).join(" · ") : ""],
                ["Clinic email", lead.email],
                ["Clinic phone", lead.phone],
                ["Address", addressLine],
                ["Country", lead.country],
                ["Preferred channel", lead.communication_method === "whatsapp" ? "WhatsApp" : lead.communication_method === "email" ? "Email" : ""],
              ]}
            />
          </Section>

          {/* ── Practice & team ──────────────────────────────────── */}
          <Section icon={<ShopOutlined />} title="Practice & team">
            <KV
              rows={[
                ["PMS", lead.pms],
                ["Clinics", lead.is_multi_clinic ? `${lead.clinic_count || "Several"} clinics` : "Single clinic"],
                ["Front desk staff", lead.frontdesk_staff_count == null ? "" : String(lead.frontdesk_staff_count)],
                ["Insurance coordinators", lead.insurance_coordinator_count == null ? "" : String(lead.insurance_coordinator_count)],
                ["Treatment coordinators", lead.treatment_coordinator_count == null ? "" : String(lead.treatment_coordinator_count)],
              ]}
            />
          </Section>

          {/* ── What they told us ────────────────────────────────── */}
          <Section icon={<MessageOutlined />} title="What they told us">
            <div className="crml-answers">
              <div>
                <div className="crml-answer-q">Tasks they want handled</div>
                {Array.isArray(lead.tasks_wanted) && lead.tasks_wanted.length ? (
                  <div className="crml-chips">
                    {lead.tasks_wanted.map((t) => <Tag key={t} color="purple">{taskLabel(t)}</Tag>)}
                  </div>
                ) : (
                  <div className="crml-answer-a is-empty">Not answered</div>
                )}
                {lead.tasks_other ? <div className="crml-answer-a" style={{ marginTop: 6 }}>{lead.tasks_other}</div> : null}
              </div>
              {Object.keys(QUESTIONS).map((k) => (
                <div key={k}>
                  <div className="crml-answer-q">{QUESTIONS[k]}</div>
                  <div className={`crml-answer-a${lead[k] ? "" : " is-empty"}`}>{lead[k] || "Not answered"}</div>
                </div>
              ))}
            </div>
          </Section>

          {/* ── Contacts ─────────────────────────────────────────── */}
          <Section icon={<UserOutlined />} title="Contacts" count={contacts.length}>
            {contacts.length ? (
              <ContactCarousel contacts={contacts} />
            ) : (
              <div className="crm-empty">No contacts yet — add a principal dentist with Edit.</div>
            )}
          </Section>

          {/* ── Invites ──────────────────────────────────────────── */}
          <Section icon={<SendOutlined />} title="Invites" count={invites.length}>
            <Table
              size="small"
              rowKey="id"
              pagination={false}
              locale={{
                emptyText: (
                  <div className="crm-empty">
                    {canInvite ? "Not invited yet — Invite sends them a registration link." : "No invites."}
                  </div>
                ),
              }}
              columns={[
                { title: "Email", dataIndex: "receiver_email", render: (v) => <span className="crm-name">{v}</span> },
                {
                  title: "State", width: 120,
                  render: (_, r) => {
                    const s = inviteStatus(r);
                    return <span className={`crm-ipill ${s.key}`}>{s.label}</span>;
                  },
                },
                {
                  title: "Expires", width: 130,
                  render: (_, r) => {
                    const s = inviteStatus(r);
                    return <span className={`crm-exp${s.key === "expired" ? " past" : ""}`}>{fmtDay(r.expiration_date)}</span>;
                  },
                },
                {
                  title: "", key: "a", align: "right", width: 150,
                  render: (_, r) => {
                    const s = inviteStatus(r);
                    return (
                      <Space>
                        <Tooltip title={r.invite_link ? "Copy invite link" : "No link"} zIndex={10050}>
                          <Button size="small" icon={<LinkOutlined />} disabled={!r.invite_link} onClick={() => copyLink(r)} />
                        </Tooltip>
                        <Button
                          size="small"
                          icon={<RedoOutlined />}
                          loading={resending === r.id}
                          disabled={s.key === "accepted" || s.key === "cancelled"}
                          onClick={() => resend(r)}
                        >
                          Resend
                        </Button>
                      </Space>
                    );
                  },
                },
              ]}
              dataSource={invites}
            />
          </Section>

          {/* ── Linked practice (once converted) ─────────────────── */}
          {practice ? (
            <Section icon={<TeamOutlined />} title="Linked practice" subtitle="this lead is now a customer">
              <div className="crml-link-row">
                <span className="crm-avatar-sm">{initialOf(practice.name)}</span>
                <button type="button" className="crml-link-name" onClick={() => onOpenPractice && onOpenPractice(practice.id)}>
                  {practice.name}
                </button>
                <span className="crml-link-kind">Practice</span>
              </div>
              {offices.map((o) => (
                <div className="crml-link-row" key={o.id}>
                  <span className="crm-avatar-sm office"><ShopOutlined /></span>
                  <button type="button" className="crml-link-name" onClick={() => onOpenOffice && onOpenOffice(o.id, practice.id)}>
                    {o.name}
                  </button>
                  <span className="crml-link-kind">Office</span>
                </div>
              ))}
              {lead.user ? <div className="crm-sub" style={{ padding: "6px 8px 2px" }}>Registered as {lead.user.email}</div> : null}
            </Section>
          ) : null}

          {/* ── Conversation ─────────────────────────────────────── */}
          <div ref={conversationRef}>
            <Section
              icon={<MailOutlined />}
              title="Conversation"
              subtitle={
                lead.booking_email_sent_at
                  ? `booking email last sent ${fmtDay(lead.booking_email_sent_at)}${lead.booking_email_sent_count > 1 ? ` · ${lead.booking_email_sent_count} sent` : ""}`
                  : "every email with this lead, newest first"
              }
            >
              <EmailFeed
                leadId={lead.id}
                leadEmail={lead.email}
                contacts={contacts}
                initialMessageId={initialMessageId}
                onSent={() => onChanged && onChanged()}
              />
            </Section>
          </div>

          {/* ── Website submissions ──────────────────────────────── */}
          <Section icon={<GlobalOutlined />} title="Website submissions" count={submissions.length}>
            {submissions.length ? (
              submissions.map((s) => <Submission key={s.id} s={s} />)
            ) : (
              <div className="crm-empty">
                {lead.source === "framer" ? "The form payload wasn't kept for this lead." : "This lead didn't come through the website form."}
              </div>
            )}
          </Section>
        </div>
      )}

      {/* Modals opened from inside a Drawer sit under it unless lifted: the app
          pins open drawers at z-index 10001. InviteCustomerModal takes no zIndex
          prop, so the lift comes from a ConfigProvider it reads its Modal
          styles from — the same 10050 the other in-drawer modals use. */}
      {lead ? (
        <ConfigProvider modal={{ styles: { wrapper: { zIndex: 10050 }, mask: { zIndex: 10050 } } }}>
          <InviteCustomerModal
            open={inviteOpen}
            onClose={() => setInviteOpen(false)}
            onCreated={changed}
            initial={{
              email: lead.email || (principal && principal.email) || "",
              practiceName: lead.practice_name || "",
              contactPersonName: (principal && principal.name) || "",
              contactPhone: lead.phone || (principal && principal.phone) || "",
            }}
            leadId={lead.id}
          />
        </ConfigProvider>
      ) : null}
      {lead ? (
        <BookingEmailModal lead={lead} open={bookingOpen} onClose={() => setBookingOpen(false)} onSent={changed} />
      ) : null}
      {lead ? (
        <NotALeadModal
          lead={lead}
          open={notLeadOpen}
          onClose={() => setNotLeadOpen(false)}
          onDone={() => { setNotLeadOpen(false); onChanged && onChanged(); onClose(); }}
        />
      ) : null}
      <LeadFormDrawer open={editOpen} lead={lead} onClose={() => setEditOpen(false)} onSaved={changed} />
    </Drawer>
  );
}
